import { PATHS } from '@client/routes';
import {
  CollectionsModel,
  LiteCollection,
  LiteCollectionType,
} from './collections.domain';
import {
  cellIconText,
  CellProps,
  cellMenu,
  CellMenuProps,
} from '../../components/TableShared/Cell';
import { cellHead } from '../../components/TableShared/CellHead';
import {
  Row,
  StandardTableProps,
} from '../../components/StandardTable/StandardTable';
import { getSalesPageUrl } from '../../helpers/landingPages';
import _ from 'lodash';
import { SmartTableProps } from '@client/new/components/SmartTable/SmartTable';
import { DragTableProps } from '@client/new/components/DragTable/DragTable';
import { toProgramLandingPageUrl } from '@client/helpers/routes';
import { CollectionTag } from './components/collectionTag';
import { ProgramLinks } from './components/programLinks';
import { extendDispatch } from '@client/new/helpers/types-utils';
import { faFlagSwallowtail } from '@fortawesome/pro-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faFlagSwallowtail as faFlagSwallowtailLight } from '@fortawesome/pro-light-svg-icons';
import { ColorsVars } from '@design-system/vars.css';

import {
  ToViewModeParams,
  ToTablePropsParams,
  CollectionsViewActionsMap,
  ViewMode,
  CollectionViewDispatcher,
  ViewState,
  ModalType,
  CollectionsViewModel,
} from './collections.viewModel';
import { toCollectionLink } from './collection-links';
import { _noop } from '@client/new/helpers/general';
import { navigate } from '@client/new/helpers/history';
import { Program } from '@playbook-technologies/common-enums';

export const toCollectionsMode = ({
  model,
  dispatch,
  selectedList,
  isCreatorLive,
  isBundleCreator,
  hasPlatformBundle,
}: ToViewModeParams): ViewMode => {
  const isEmpty = _.valuesIn(model.collections).every((i) => _.isEmpty(i));
  if (isEmpty) return { type: 'empty' };
  const value = toSmartTableProps({
    selectedList: selectedList,
    model,
    dispatch,
    isCreatorLive,
    isBundleCreator,
    hasPlatformBundle,
  });

  const hasDraftsOnly =
    !_.isEmpty(model.collections.draft) &&
    _.isEmpty(model.collections.published) &&
    _.isEmpty(model.collections.archived);
  if (hasDraftsOnly) return { type: 'drafts-only', value };
  return { type: 'standard', value };
};

export const toTableProps = ({
  selectedList,
  model,
  dispatch,
  isCreatorLive,
  isBundleCreator,
  hasPlatformBundle,
}: ToTablePropsParams): StandardTableProps<number> => {
  const data =
    selectedList === 'all'
      ? _.sortBy(
          [
            ...model.collections.published,
            ...model.collections.draft,
            ...model.collections.archived,
          ],
          ['index'],
        )
      : _.sortBy(model.collections[selectedList], ['index']);

  const rows = data.map((col) => {
    const collectionState = model.programStateMap[col.programId];

    const cells: Array<CellProps> =
      collectionState && collectionState.type !== 'fetched'
        ? [{ type: 'Busy', value: {} } as const]
        : toCells({
            influencerId: model.influencerId,
            col,
            dispatch,
            isCreatorLive,
            isBundleCreator,
            hasPlatformBundle,
          });
    return { id: col.programId + '', cells } as unknown as Row<number>;
  });

  return { rows, schema: tableSchema };
};

export const toSmartTableProps = (
  p: ToTablePropsParams,
): SmartTableProps<number> => {
  const { dispatch } = extendDispatch<CollectionsViewActionsMap>(p.dispatch);

  const tableProps = toTableProps(p);
  const isDraggable = p.selectedList === 'all';
  const onChange: DragTableProps<number>['onChange'] = (rows) =>
    dispatch(
      'reorder',
      rows.map((r) => Number(r.id)),
    );
  return isDraggable
    ? { type: 'draggable', value: { ...tableProps, onChange } }
    : { type: 'standard', value: tableProps };
};

export const tableSchema: StandardTableProps<number>['schema'] = [
  { label: 'program name', width: 'stretchable' },
  { label: 'status', width: 150 },
  { label: 'rating', width: 100, justify: 'center' },
  { label: 'begins', width: 100, justify: 'center' },
  { label: 'completions', width: 115, justify: 'center' },
  { label: 'comments', width: 100, justify: 'center' },
  { label: 'links', width: 150, justify: 'center' },
  { label: 'actions', width: 110, justify: 'right' },
];

export const getMenu = ({
  programId,
  type,
  accessType,
  dispatch,
  influencerId,
  isBundleCreator,
  hasPlatformBundle,
}: {
  programId: number;
  type: LiteCollectionType;
  accessType: LiteCollection['accessType'];
  dispatch: CollectionViewDispatcher;
  influencerId: number;
  isBundleCreator: boolean;
  hasPlatformBundle: boolean;
}): CellMenuProps['value']['items'] => {
  const isOtp = accessType === 'exclusive';

  const duplicate = [
    {
      text: 'Duplicate',
      onClick: () => dispatch({ type: 'duplicateProgram', payload: programId }),
    },
  ];

  if (type === 'draft') {
    return [
      {
        text: 'Edit',
        to: PATHS.CATEGORY_EDIT(programId, influencerId),
      },
      ...duplicate,
      {
        text: 'Delete',
        onClick: () => dispatch({ type: 'deleteModal', payload: programId }),
      },
    ];
  }
  if (type === 'archived') {
    return [
      {
        text: isOtp ? 'Preview' : 'Edit',
        to: PATHS.CATEGORY_EDIT(programId, influencerId),
      },
      ...duplicate,
      ...(!isOtp
        ? [
            {
              text: 'Publish',
              onClick: () =>
                dispatch({
                  type:
                    isBundleCreator &&
                    hasPlatformBundle &&
                    accessType === Program.AccessType.Premium.value
                      ? 'showPublishToBundleModal'
                      : 'publishProgram',
                  payload: programId,
                }),
            },
          ]
        : []),
    ];
  }
  return [
    {
      text: 'Edit',
      to: PATHS.CATEGORY_EDIT(programId, influencerId),
    },
    ...duplicate,
    {
      text: 'Archive',
      onClick: () =>
        dispatch({
          type:
            isBundleCreator && accessType === Program.AccessType.Premium.value
              ? 'showRemoveFromBundleModal'
              : 'archiveModal',
          payload: programId,
        }),
    },
  ];
};

export const toLandingPageLink = (
  col: Pick<LiteCollection, 'type' | 'customURL' | 'programId' | 'title'>,
  influencerId: number,
): {
  to: string;
  label: string;
  external?: boolean;
} => {
  return col.type === 'draft'
    ? {
        label: 'Customize Page',
        to: PATHS.PROGRAM_EDIT(col.programId, influencerId, 'sales-page'),
      }
    : {
        label: 'Preview Page',
        to: getSalesPageUrl({
          customURL: col.customURL,
          id: col.programId,
          name: col.title,
        }),
        external: true,
      };
};

export type ToCellsParams = {
  influencerId: number;
  col: LiteCollection;
  dispatch: CollectionViewDispatcher;
  isCreatorLive: boolean;
  isBundleCreator: boolean;
  hasPlatformBundle: boolean;
};

const getChallengeProps = (
  col: LiteCollection,
):
  | { icon: IconProp; iconTooltip: string; iconColor: ColorsVars }
  | undefined => {
  if (!col.hasChallenges) return undefined;
  if (col.hasOngoingChallenges)
    return {
      icon: faFlagSwallowtail,
      iconTooltip: 'This program has ongoing challenge',
      iconColor: 'G-100',
    };
  return {
    icon: faFlagSwallowtailLight,
    iconTooltip: 'This program have past or future challenges',
    iconColor: 'W-100',
  };
};

export const toCells = ({
  col,
  dispatch,
  influencerId,
  isCreatorLive,
  isBundleCreator,
  hasPlatformBundle,
}: ToCellsParams): Row['cells'] => {
  const normalize = (v: number) => (col.type === 'draft' ? '-' : `${v}`);

  return [
    cellHead({
      title: col.title,
      link: toLandingPageLink(col, influencerId),
      url: col.thumbnailUrl,
      ...getChallengeProps(col),
    }),
    { type: 'Custom', value: CollectionTag({ type: col.type }) },
    cellIconText(normalize(col.rating), undefined, {
      classNameIcon: 'text-gray-5',
    }),
    cellIconText(normalize(col.begins), undefined, {
      classNameIcon: 'text-gray-5',
    }),
    cellIconText(normalize(col.completions), undefined, {
      classNameIcon: 'text-gray-5',
    }),
    cellIconText(normalize(col.comments), undefined, {
      classNameIcon: 'text-gray-5',
    }),
    {
      type: 'Custom',
      value: ProgramLinks({
        onClick: () => dispatch({ type: 'linksModal', payload: col }),
        isCreatorLive,
      }),
    },
    cellMenu({
      items: getMenu({
        programId: col.programId,
        type: col.type,
        accessType: col.accessType,
        dispatch,
        influencerId,
        isBundleCreator,
        hasPlatformBundle,
      }),
    }),
  ];
};

const linkFilter = (
  link: { title: string; url: string; description: string } | undefined,
) => link !== undefined;

export const getProgramLinks = (p: ViewState['openModal']) => {
  if (p.type !== 'linksModal') return undefined;

  const programUrl = toProgramLandingPageUrl({
    id: p.value.programId,
    name: p.value.title,
    influencer: {
      customURL: p.value.customURL,
    },
  });

  const afterDesc = !p.value.isVisible
    ? "You can copy links any time and use it for example to include on external pages, but they won't work for your users until your Program is published. "
    : '';

  if (p.value.accessType === 'exclusive') {
    return {
      programName: p.value.title,
      afterDesc,
      links: [
        toCollectionLink('programLandingPage', programUrl),
        toCollectionLink('checkoutPage', programUrl + '/checkout'),
        toCollectionLink('programDeepLink', p.value.deeplinkUrl || ''),
        toCollectionLink('challenge', p.value.challengeDeeplinkURL),
      ].filter(linkFilter),
    };
  }
  return {
    programName: p.value.title,
    afterDesc,
    links: [
      toCollectionLink('programLandingPage', programUrl),
      toCollectionLink('programDeepLink', p.value.deeplinkUrl || ''),
      toCollectionLink('challenge', p.value.challengeDeeplinkURL),
    ].filter(linkFilter),
  };
};

export const getSubLinks = (
  customURL: string | undefined,
  isBundleCreator: boolean,
) => {
  if (!customURL) return undefined;
  const landingPageLink = customURL
    ? `${import.meta.env.VITE_LANDING_PAGE_URL}/${customURL}`
    : '';

  const bundleCreatorLinks = [
    toCollectionLink('channelLandingPage', landingPageLink),
    toCollectionLink('subscriptionCheckoutPage', landingPageLink + '/checkout'),
  ];
  if (isBundleCreator) {
    return { links: bundleCreatorLinks.filter(linkFilter) };
  }
  const platformCreatorLinks = [
    ...bundleCreatorLinks,
    toCollectionLink('giftingPage', landingPageLink + '/gift'),
  ];
  return {
    links: platformCreatorLinks.filter(linkFilter),
  };
};

export const getEmptyState = (
  model: CollectionsModel,
  dispatch: CollectionViewDispatcher,
  openModal: ModalType,
): CollectionsViewModel['emptyState'] => ({
  otp: {
    isGuideOpen: openModal.type === 'openGuide',
    closeGuide: () => dispatch({ type: 'closeGuide' }),
    openGuide: () => dispatch({ type: 'openGuide' }),
    name: model.influencerName,
    guideLink: GUIDE_LINKS.otp,
  },
  subscription: {
    name: model.influencerName,
    guideLink: GUIDE_LINKS.subscription,
    create: () => dispatch({ type: 'createProgram' }),
  },
});

const GUIDE_LINKS = {
  otp: 'https://help.playbookapp.io/en/collections/9730963-set-your-offering',
  subscription:
    'https://help.playbookapp.io/en/articles/9693285-subscription-business-what-a-successful-experience-looks-like',
};
